'use client';

import { useEffect } from 'react';

const getHeaderVisibleHeight = () => {
  if (typeof document === 'undefined') return 0;

  const fromBody = parseFloat(
    getComputedStyle(document.body).getPropertyValue('--header-visible-height')
  );

  if (fromBody) return fromBody;

  return (
    parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--fixed-header-height')
    ) || 0
  );
};

interface UseCloseListboxUnderHeaderProps {
  isOpen: boolean;
  triggerRef?: React.RefObject<Element | null>;
  onClose: () => void;
  enabled?: boolean;
}

export const useCloseListboxUnderHeader = ({
  isOpen,
  triggerRef,
  onClose,
  enabled = true
}: UseCloseListboxUnderHeaderProps) => {
  useEffect(() => {
    if (!enabled || !isOpen) return;

    let frameId = 0;

    const closeIfUnderHeader = () => {
      frameId = 0;
      if (!triggerRef?.current) return;

      const { top } = triggerRef.current.getBoundingClientRect();

      if (top < getHeaderVisibleHeight()) {
        onClose();
      }
    };

    const requestCheck = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(closeIfUnderHeader);
    };

    requestCheck();
    window.addEventListener('scroll', requestCheck, { passive: true, capture: true });
    window.addEventListener('resize', requestCheck);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      window.removeEventListener('scroll', requestCheck, { capture: true });
      window.removeEventListener('resize', requestCheck);
    };
  }, [enabled, isOpen, onClose, triggerRef]);
};
